import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Popover, Select, SelectItem } from '@scholastic/mockingjay';
import InfoIcon from '@data/assets/bold-circle-info.svg';
import { Table } from '@data/components/basic/Table/Table';
import type { TableColumn, TableRow } from '@data/components/basic/Table/types';
import {
  ProficiencyLegend,
  renderLevelCell,
  renderMostStudentsCell,
} from '@data/components/basic/ProficiencySwatch/ProficiencySwatch';
import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import { SkillDrawer } from '@data/components/report/SkillDrawer/SkillDrawer';
import {
  MetricTableType,
  type MetricsTableV2Response,
  useMetricsTableV2Api,
} from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { useAllClassSkills } from '@data/hooks/use-report-tables/use-all-class-skills/use-all-class-skills';
import { useResetTableOnFilterChange } from '@data/hooks/use-report-tables/use-reset-table-on-filter-change/use-reset-table-on-filter-change';
import { useExportToCSVHandler } from '@data/hooks/use-report-tables/use-export-to-csv-handler/use-export-to-csv-handler';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { useTeacherMetricsArgs } from '@data/hooks/use-report-tables/use-teacher-metrics-args/use-teacher-metrics-args';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { buildOrderBy } from '@data/utils/build-order-by';
import { filterTableData } from '@data/utils/filter-table-data';
import {
  MOST_STUDENTS_KEY,
  withMostStudentsColumn,
} from '@data/utils/proficiency/proficiency';
import { buildRowTree } from '@data/utils/row-tree/row-tree';

const SCORE_KEY = 'SCORE';
const SKILL_PARAM = 'skillId';
const BAND_KEY = 'BAND';

interface SkillOption {
  id: number;
  name: string;
  score: number | null;
}

/** The skill groups — the skills table's top-level rows — A→Z. */
function getSkillGroups(data: MetricsTableV2Response | undefined) {
  if (!data) return [];
  const columns = data.columns as TableColumn[];
  const idIndex = columns.findIndex((col) => col.key === 'NODE_ID');
  const nameIndex = columns.findIndex((col) => col.key === 'SKILL');
  const scoreIndex = columns.findIndex((col) => col.key === SCORE_KEY);
  return buildRowTree(columns, data.rows)
    .rows.map((row): SkillOption => {
      const name = String(row[nameIndex] ?? '');
      const score = scoreIndex < 0 ? null : row[scoreIndex];
      return {
        id: Number(row[idIndex]),
        name: name.charAt(0).toUpperCase() + name.slice(1),
        score: score == null ? null : Number(score),
      };
    })
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
}

/**
 * The group with the lowest Average — the top row of Performance by Skill.
 * Groups with no Average are skipped and a tie goes to the first A→Z; with no
 * Average at all, the first group.
 */
function getDefaultSkillGroup(groups: SkillOption[]) {
  return (
    groups.reduce<SkillOption | undefined>(
      (lowest, group) =>
        group.score != null &&
        (lowest?.score == null || group.score < lowest.score)
          ? group
          : lowest,
      undefined
    ) ?? groups[0]
  );
}

const addMostStudents = (columns: TableColumn[], rows: TableRow[]) =>
  withMostStudentsColumn(columns, rows, SCORE_KEY);

/** Leads each exported row with the skill it reports on. */
function withSkillColumn(
  columns: TableColumn[],
  rows: TableRow[],
  skill: string | undefined
) {
  return {
    columns: [
      { type: 'string', label: 'Skill', key: 'SKILL' } as TableColumn,
      ...columns,
    ],
    rows: rows.map((row) => [skill ?? null, ...row]),
  };
}

/** A file-name-safe form of a name, e.g. "Letter-sound correspondence" → "letter-sound-correspondence". */
const toSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

function renderGroupCell(value: TableRow[number], column: TableColumn) {
  if (column.key === MOST_STUDENTS_KEY) return renderMostStudentsCell(value);
  if (column.key === BAND_KEY) return renderLevelCell(value);
  return undefined;
}

/**
 * Results on one skill group, picked from the groups the skills table found:
 * each class's, or in one class each student's. Waits on that table's request,
 * as the groups come from it.
 */
export function SkillClassTable() {
  const { drilldown, productId: edApp } = useReportOutletContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const byStudent = drilldown === Drilldown.Class;
  const title = byStudent
    ? 'Skill performance by student'
    : 'Skill performance by class';
  const { args, ready } = useTeacherMetricsArgs();
  const skills = useAllClassSkills();

  const groups = useMemo(() => getSkillGroups(skills.data), [skills.data]);
  // Kept on the URL so drilling in and out, or sharing the link, keeps the pick.
  const skillId = Number(searchParams.get(SKILL_PARAM));

  // The lowest-scoring group, until one is picked that the groups still hold.
  const selected =
    groups.find((group) => group.id === skillId) ??
    getDefaultSkillGroup(groups);
  const selectedId = selected?.id;
  // Said once a pick from the drawer applies, as the table reloads in place.
  const [announcement, setAnnouncement] = useState('');

  const { tableProps, searchTerms, userSorted, reset } = useTableControls({
    defaultSortColumn: SCORE_KEY,
    defaultSortDirection: 'desc',
  });

  useResetTableOnFilterChange(
    drilldown,
    searchParams.getAll('classId').join(','),
    searchParams.getAll('studentId').join(','),
    reset
  );

  const sharedArgs = {
    ...args,
    metricType: byStudent
      ? MetricTableType.IndividualClassSkillGroup
      : MetricTableType.AllClassSkillGroup,
    skillNodeId: selectedId,
    pageSize: tableProps.pageSize,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
  };

  const { data, loading, error } = useMetricsTableV2Api({
    ...sharedArgs,
    page: tableProps.page,
    enabled: ready && skills.isSuccess && selectedId != null,
  });

  const { refetch: refetchExportData } = useMetricsTableV2Api({
    ...sharedArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const { columns, rows } = useMemo(() => {
    const { visibleColumns, visibleRows } = filterTableData(data);
    return byStudent
      ? { columns: visibleColumns, rows: visibleRows }
      : addMostStudents(visibleColumns, visibleRows);
  }, [data, byStudent]);

  const skillName = selected?.name;
  const toExport = useCallback(
    (cols: TableColumn[], exportRows: TableRow[]) => {
      const exported = byStudent
        ? { columns: cols, rows: exportRows }
        : addMostStudents(cols, exportRows);
      return withSkillColumn(exported.columns, exported.rows, skillName);
    },
    [skillName, byStudent]
  );

  const handleExportToCSV = useExportToCSVHandler(
    refetchExportData,
    undefined,
    edApp,
    // The file holds only the picked group, so it is named for it.
    [
      byStudent ? 'skill-performance-by-student' : 'skill-performance-by-class',
      skillName && toSlug(skillName),
    ]
      .filter(Boolean)
      .join('_'),
    toExport
  );

  const selectSkill = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SKILL_PARAM, value);
        return next;
      },
      { replace: true, preventScrollReset: true }
    );
    tableProps.onPageChange(1);
  };

  // Into a class's students, or one student's skills, on the same group and
  // any custom date range.
  const handleDrilldown = (id: string | number) => {
    const next = byStudent
      ? new URLSearchParams({
          classId: searchParams.get('classId') ?? '',
          studentId: String(id),
        })
      : new URLSearchParams({ classId: String(id) });
    if (selectedId != null) next.set(SKILL_PARAM, String(selectedId));
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      next.set('from', from);
      next.set('to', to);
    }
    const level = byStudent ? 'student' : 'class';
    navigate(`/data/${edApp}/skill-performance/${level}?${next.toString()}`);
  };

  if (!skills.isSuccess || selectedId == null) return null;

  return (
    <section
      aria-labelledby="skill-class-heading"
      className="flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <h3
          id="skill-class-heading"
          className="text-lg font-bold text-neutral-1300 tablet:text-xl"
        >
          {title}
        </h3>
        {data?.toolTips && (
          <Popover
            trigger={
              <button
                type="button"
                aria-label={`${title} information`}
                className="flex items-center"
              >
                <InfoIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            }
          >
            {data.toolTips}
          </Popover>
        )}
      </div>
      <ProficiencyLegend />
      <Table
        {...tableProps}
        loading={loading}
        columns={columns}
        rows={rows}
        totalCount={data?.totalRecords ?? 0}
        renderCell={renderGroupCell}
        leftAlignedColumns={[BAND_KEY, MOST_STUDENTS_KEY]}
        onDrilldown={handleDrilldown}
        keepToolbarWhileLoading
        toolbarCaption={
          <div className="flex items-start justify-between gap-3">
            <p className="text-base font-semibold text-neutral-1300">
              {selected?.name}
            </p>
            {/* Below laptop the toolbar's select gives way to this drawer. */}
            <div className="laptop:hidden">
              <SkillDrawer
                options={groups}
                selectedId={selectedId}
                onApply={(id) => {
                  selectSkill(String(id));
                  const name = groups.find((g) => g.id === id)?.name;
                  setAnnouncement(`${title} updated for ${name}`);
                }}
              />
            </div>
          </div>
        }
        scrollRegionLabel={`${title} table, scrollable`}
        toolbarActions={
          // Fixed at the design's width. The option's name is a block of its
          // own, made inline so "Skill: <name>" is one line cut off at the end.
          <div className="order-last hidden w-full shrink-0 @[500px]/toolbar:order-none @[500px]/toolbar:w-56 laptop:block [&>div]:w-full">
            <Select
              placeholder="Skill"
              classNames={{
                trigger:
                  "!h-12 !min-w-0 [&>span:first-child]:block [&>span:first-child]:min-w-0 [&>span:first-child]:truncate [&>span:first-child]:text-left [&>span:first-child]:before:content-['Skill:_'] [&>span:first-child>span]:inline",
              }}
              value={String(selectedId)}
              onValueChange={selectSkill}
            >
              {groups.map((group) => (
                <SelectItem key={group.id} value={String(group.id)}>
                  {group.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        }
        onExportToCSV={handleExportToCSV}
        error={error}
      />
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </section>
  );
}
